<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required, maxLength, minValue } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { PAYMENT_METHOD_MODULE } from "@/state/modules/paymentMethod";
import { paymentMethodMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  page() {
    return {
      title: this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module.paymentMethod")])
        : i18n.t("label.createX", [i18n.t("module.paymentMethod")]),
    };
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      form: {
        name: "",
        type: "cash",
        details: [],
        instructions: "",
        is_active: true,
        sort_order: 0,
        image_base64: null,
        remove_image: false,
      },
      existingImageUrl: null,
      submitting: false,
      selectedType: null,
      selectedStatus: null,
    };
  },
  validations() {
    return {
      form: {
        name: { required, maxLength: maxLength(200) },
        sort_order: { minValue: minValue(0) },
      },
    };
  },
  computed: {
    ...mapState(PAYMENT_METHOD_MODULE, ["paymentMethods"]),
    typeOptions() {
      return [
        { value: "cash", text: i18n.t("paymentMethod.typeCash") },
        { value: "bank", text: i18n.t("paymentMethod.typeBank") },
        { value: "qr", text: i18n.t("paymentMethod.typeQr") },
      ];
    },
    statusOptions() {
      return [
        { value: true, text: i18n.t("label.active") },
        { value: false, text: i18n.t("label.inactive") },
      ];
    },
    isEdit() {
      return !!this.$route.params.uuid;
    },
    /** Only cash puts money in the agent's own hands — worth saying on the form. */
    isCash() {
      return this.form.type === "cash";
    },
    title() {
      return this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module.paymentMethod")])
        : i18n.t("label.createX", [i18n.t("module.paymentMethod")]);
    },
    items() {
      return [
        { text: i18n.t("module.admin"), href: "/" },
        { text: i18n.t("module.paymentMethod"), href: "/payment-methods" },
        { text: this.title, active: true },
      ];
    },
  },
  async created() {
    await this.getAllPaymentMethods({ params: { include_inactive: 1 } });

    if (this.isEdit) {
      const row = (this.paymentMethods.data || []).find(
        (m) => String(m.uuid) === String(this.$route.params.uuid)
      );
      if (row) {
        this.form = {
          name: row.name || "",
          type: row.type || "cash",
          // A copy, so editing the form does not mutate the store's row.
          details: (row.details || []).map((d) => ({ label: d.label, value: d.value })),
          instructions: row.instructions || "",
          is_active: row.isActive,
          sort_order: row.sortOrder ?? 0,
          image_base64: null,
          remove_image: false,
        };
        this.existingImageUrl = row.imageUrl;
      }
    }

    this.selectedType =
      this.typeOptions.find((t) => t.value === this.form.type) || this.typeOptions[0];
    this.selectedStatus =
      this.statusOptions.find((s) => s.value === this.form.is_active) || this.statusOptions[0];
  },
  methods: {
    ...paymentMethodMethods,
    onTypeChange(t) {
      this.selectedType = t;
      this.form.type = t ? t.value : "cash";
    },
    onStatusChange(s) {
      this.selectedStatus = s;
      this.form.is_active = s ? s.value : true;
    },
    addDetail() {
      this.form.details.push({ label: "", value: "" });
    },
    removeDetail(index) {
      this.form.details.splice(index, 1);
    },
    onImageSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        this.form.image_base64 = reader.result;
        this.existingImageUrl = reader.result;
        this.form.remove_image = false;
      };
      reader.readAsDataURL(file);
    },
    clearImage() {
      this.form.image_base64 = null;
      this.existingImageUrl = null;
      this.form.remove_image = true;
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      // Blank rows are the residue of an added-then-abandoned detail; sending
      // them would fail validation on a label the user never meant to fill.
      const payload = {
        ...this.form,
        details: this.form.details.filter((d) => String(d.label || "").trim() !== ""),
      };

      this.submitting = true;
      try {
        const result = this.isEdit
          ? await this.updatePaymentMethod({ uuid: this.$route.params.uuid, data: payload })
          : await this.createPaymentMethod({ data: payload });

        // Only leave the form once the save actually went through.
        if (result) this.$router.push({ name: "Payment-method-listing" });
      } finally {
        this.submitting = false;
      }
    },
    cancel() {
      this.$router.push({ name: "Payment-method-listing" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('label.name')">
                    <b-form-input
                      v-model="form.name"
                      :state="$v.form.name.$dirty ? !$v.form.name.$invalid : null"
                      :placeholder="$t('label.name')"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('paymentMethod.type')" :description="$t('paymentMethod.cashHint')">
                    <multiselect
                      :value="selectedType"
                      :options="typeOptions"
                      label="text"
                      track-by="value"
                      :allow-empty="false"
                      :show-labels="false"
                      open-direction="below"
                      @input="onTypeChange"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('paymentMethod.details')" :description="$t('paymentMethod.detailsHint')">
                    <div
                      v-for="(d, i) in form.details"
                      :key="`detail-${i}`"
                      class="d-flex align-items-start mb-2"
                    >
                      <b-form-input
                        v-model="d.label"
                        class="mr-2"
                        :placeholder="$t('paymentMethod.detailLabel')"
                      />
                      <b-form-input
                        v-model="d.value"
                        class="mr-2"
                        :placeholder="$t('paymentMethod.detailValue')"
                      />
                      <b-button variant="outline-danger" @click="removeDetail(i)">
                        <i class="mdi mdi-close"></i>
                      </b-button>
                    </div>
                    <b-button variant="outline-secondary" size="sm" @click="addDetail">
                      <i class="mdi mdi-plus mr-1"></i>{{ $t('paymentMethod.addDetail') }}
                    </b-button>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group
                    :label="$t('paymentMethod.instructions')"
                    :description="$t('paymentMethod.instructionsHint')"
                  >
                    <b-form-textarea v-model="form.instructions" rows="3" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('paymentMethod.image')" :description="$t('paymentMethod.imageHint')">
                    <div v-if="existingImageUrl" class="mb-2">
                      <img
                        :src="existingImageUrl"
                        style="max-height: 160px; max-width: 100%; object-fit: contain;"
                        :alt="$t('paymentMethod.image')"
                      />
                      <div>
                        <b-button size="sm" variant="link" class="text-danger p-0 mt-1" @click="clearImage">
                          <i class="mdi mdi-close mr-1"></i>{{ $t('paymentMethod.removeImage') }}
                        </b-button>
                      </div>
                    </div>
                    <b-form-file accept="image/*" :placeholder="$t('label.select')" @change="onImageSelected" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('paymentMethod.sortOrder')" :description="$t('paymentMethod.sortOrderHint')">
                    <b-form-input
                      v-model.number="form.sort_order"
                      type="number"
                      min="0"
                      step="1"
                      :state="form.sort_order != null && Number(form.sort_order) < 0 ? false : null"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('label.status')">
                    <multiselect
                      :value="selectedStatus"
                      :options="statusOptions"
                      label="text"
                      track-by="value"
                      :allow-empty="false"
                      :show-labels="false"
                      open-direction="below"
                      @input="onStatusChange"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="mt-3">
                <b-button type="submit" variant="primary" :disabled="submitting">
                  <b-spinner v-if="submitting" small class="mr-1" />
                  {{ $t('label.submit') }}
                </b-button>
                <b-button variant="light" class="ml-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
